import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getPaymentMethodBreakdown,
  getAccountPaymentMethodBreakdown,
  type PaymentMethodBreakdownItem,
  type AccountPaymentMethodBreakdownItem,
} from '../../../lib/paymentMethodUtils';
import { PAYMENT_METHOD_ICONS, PAYMENT_METHOD_COLORS } from '../../../lib/paymentMethods';

// ─── Color palette for chart bars (no blue/purple per style rules) ──────────
const METHOD_BAR_COLORS: Record<string, string> = {
  pix:           'bg-teal-400',
  boleto:        'bg-orange-400',
  ted:           'bg-sky-400',
  doc:           'bg-cyan-400',
  debito:        'bg-rose-400',
  dinheiro:      'bg-emerald-400',
  saque:         'bg-amber-400',
  transferencia: 'bg-teal-300',
  outro:         'bg-gray-300',
};

const METHOD_BADGE_COLORS: Record<string, string> = {
  pix:           'bg-teal-50 text-teal-700',
  boleto:        'bg-orange-50 text-orange-700',
  ted:           'bg-sky-50 text-sky-700',
  doc:           'bg-cyan-50 text-cyan-700',
  debito:        'bg-rose-50 text-rose-700',
  dinheiro:      'bg-emerald-50 text-emerald-700',
  saque:         'bg-amber-50 text-amber-700',
  transferencia: 'bg-teal-50 text-teal-600',
  outro:         'bg-gray-100 text-gray-600',
};

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ─── Sub-components ──────────────────────────────────────────────────────────

function SkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-7 h-7 bg-gray-100 rounded-lg animate-pulse shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="w-24 h-3.5 bg-gray-100 rounded animate-pulse" />
            <div className="w-full h-2 bg-gray-100 rounded-full animate-pulse" />
          </div>
          <div className="w-20 h-4 bg-gray-100 rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
      <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center">
        <i className="ri-pie-chart-2-line text-2xl text-gray-300" />
      </div>
      <p className="text-sm text-gray-400">{message}</p>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

interface Props {
  dateFrom?: string;
  dateTo?: string;
}

export function PaymentMethodBreakdown({ dateFrom, dateTo }: Props) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [breakdown, setBreakdown] = useState<PaymentMethodBreakdownItem[]>([]);
  const [accountBreakdown, setAccountBreakdown] = useState<AccountPaymentMethodBreakdownItem[]>([]);
  const [activeTab, setActiveTab] = useState<'method' | 'account'>('method');

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      getPaymentMethodBreakdown(user.id, dateFrom, dateTo),
      getAccountPaymentMethodBreakdown(user.id, dateFrom, dateTo),
    ]).then(([bd, abd]) => {
      setBreakdown(bd);
      setAccountBreakdown(abd);
      setLoading(false);
    });
  }, [user, dateFrom, dateTo]);

  const grandTotal = breakdown.reduce((s, b) => s + b.totalAmount, 0);
  const grandCount = breakdown.reduce((s, b) => s + b.count, 0);

  // Group accountBreakdown by accountId for the account view
  const byAccount = accountBreakdown.reduce<Record<string, AccountPaymentMethodBreakdownItem[]>>((acc, item) => {
    if (!acc[item.accountId]) acc[item.accountId] = [];
    acc[item.accountId].push(item);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-[15px] font-700 text-gray-900">Formas de Pagamento</h3>
            <p className="text-[12px] text-gray-400 mt-0.5">
              Análise de despesas pagas via conta bancária por método de pagamento
            </p>
          </div>
          {/* Mini summary */}
          {!loading && grandTotal > 0 && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[11px] text-gray-400 font-500">Total analisado</p>
                <p className="text-sm font-700 text-gray-800">{fmt(grandTotal)}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-gray-400 font-500">Lançamentos</p>
                <p className="text-sm font-700 text-gray-800">{grandCount}</p>
              </div>
            </div>
          )}
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mt-4 w-fit">
          {([
            { key: 'method',  label: 'Por método',  icon: 'ri-swap-box-line' },
            { key: 'account', label: 'Por conta',   icon: 'ri-bank-line' },
          ] as const).map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-600 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === t.key ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${t.icon} text-[13px]`} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {/* ── Tab: Por método ── */}
        {activeTab === 'method' && (
          <>
            {loading ? (
              <SkeletonRows count={5} />
            ) : breakdown.length === 0 ? (
              <EmptyState message="Nenhum lançamento pago via conta com forma de pagamento registrada no período." />
            ) : (
              <div className="space-y-1">
                {/* Stacked bar visual */}
                <div className="flex h-3 rounded-full overflow-hidden mb-5">
                  {breakdown.map(b => {
                    const pct = grandTotal > 0 ? (b.totalAmount / grandTotal) * 100 : 0;
                    return (
                      <div
                        key={b.method}
                        className={`${METHOD_BAR_COLORS[b.method] ?? 'bg-gray-300'} transition-all`}
                        style={{ width: `${pct}%` }}
                        title={`${b.label}: ${fmt(b.totalAmount)}`}
                      />
                    );
                  })}
                </div>

                {/* Legend row */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {breakdown.map(b => (
                    <span
                      key={b.method}
                      className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-600 ${METHOD_BADGE_COLORS[b.method] ?? 'bg-gray-100 text-gray-600'}`}
                    >
                      <i className={`${PAYMENT_METHOD_ICONS[b.method] ?? 'ri-question-line'} text-[10px]`} />
                      {b.label}
                    </span>
                  ))}
                </div>

                {/* Rows */}
                {breakdown.map(b => {
                  const pct = grandTotal > 0 ? (b.totalAmount / grandTotal) * 100 : 0;
                  return (
                    <div key={b.method} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                      {/* Icon */}
                      <div className={`w-8 h-8 flex items-center justify-center rounded-xl shrink-0 ${METHOD_BADGE_COLORS[b.method] ?? 'bg-gray-100'}`}>
                        <i className={`${PAYMENT_METHOD_ICONS[b.method] ?? 'ri-question-line'} text-sm ${PAYMENT_METHOD_COLORS[b.method] ?? 'text-gray-500'}`} />
                      </div>

                      {/* Bar + label */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[13px] font-600 text-gray-700">{b.label}</span>
                          <span className="text-[11px] text-gray-400 font-500 shrink-0 ml-2">
                            {b.count} lançamento{b.count !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${METHOD_BAR_COLORS[b.method] ?? 'bg-gray-300'} transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>

                      {/* Value + % */}
                      <div className="text-right shrink-0 min-w-[90px]">
                        <p className="text-[13px] font-700 text-gray-800">{fmt(b.totalAmount)}</p>
                        <p className="text-[10px] text-gray-400">{pct.toFixed(1)}%</p>
                      </div>
                    </div>
                  );
                })}

                {/* Total row */}
                <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-100">
                  <span className="text-xs font-600 text-gray-500 uppercase tracking-wide">Total via conta</span>
                  <div className="text-right">
                    <span className="text-sm font-700 text-gray-900">{fmt(grandTotal)}</span>
                    <span className="text-[11px] text-gray-400 ml-2">({grandCount} lançamentos)</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Tab: Por conta ── */}
        {activeTab === 'account' && (
          <>
            {loading ? (
              <SkeletonRows count={4} />
            ) : Object.keys(byAccount).length === 0 ? (
              <EmptyState message="Nenhuma conta com forma de pagamento registrada no período." />
            ) : (
              <div className="space-y-5">
                {Object.entries(byAccount).map(([accountId, items]) => {
                  const accTotal = items.reduce((s, i) => s + i.totalAmount, 0);
                  const accName  = items[0]?.accountName ?? 'Conta';
                  return (
                    <div key={accountId}>
                      {/* Account header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-lg">
                            <i className="ri-bank-line text-sm text-emerald-500" />
                          </div>
                          <span className="text-sm font-700 text-gray-800">{accName}</span>
                        </div>
                        <span className="text-sm font-700 text-gray-700">{fmt(accTotal)}</span>
                      </div>

                      {/* Methods for this account */}
                      <div className="space-y-2 pl-9">
                        {items.map(item => {
                          const pct = accTotal > 0 ? (item.totalAmount / accTotal) * 100 : 0;
                          return (
                            <div key={item.method} className="flex items-center gap-2">
                              <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full shrink-0 ${METHOD_BADGE_COLORS[item.method] ?? 'bg-gray-100 text-gray-600'}`}>
                                {item.label}
                              </span>
                              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${METHOD_BAR_COLORS[item.method] ?? 'bg-gray-300'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-[12px] font-600 text-gray-700 shrink-0 min-w-[75px] text-right">
                                {fmt(item.totalAmount)}
                              </span>
                              <span className="text-[11px] text-gray-400 shrink-0 w-10 text-right">
                                {pct.toFixed(0)}%
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      <div className="h-px bg-gray-100 mt-4" />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
